// Thin API helpers for the admin ingestor pages. Centralises path strings
// and the "send only changed fields" pattern.

import { api } from '../../../lib/api';
import type {
  ConditionTree,
  IngestorActor,
  IngestorInstance,
  UnmappedActor,
  IngestorType,
  EventType,
  Badge,
  BadgeRarity,
  ScoringRule,
  ScoringRuleSplit,
} from './types';

export interface CreateInstanceBody {
  type: string;
  team_id: string;
  name: string;
  config: Record<string, unknown>;
  secret: Record<string, string>;
}

export interface UpdateInstanceBody {
  name?: string;
  config?: Record<string, unknown>;
  secret?: Record<string, string>;
  enabled?: boolean;
}

export interface TestConnectionResult {
  ok: boolean;
  error?: string;
  note?: string;
  [key: string]: unknown;
}

export interface IngestorEventRow {
  id: string;
  instance_id: string;
  external_id: string;
  type: string;
  actor_external: string | null;
  target_external: string | null;
  occurred_at: string;
  received_at: string;
  scored_at: string | null;
  // M5.7+: raw-payload TTL fields. `raw_present` is true if
  // the unredacted vendor payload is still on disk; `raw_frozen`
  // is true if the admin pinned the row to exclude it from the
  // TTL job. Surfaced in the events table on the ingestor
  // detail page.
  raw_present: boolean;
  raw_frozen: boolean;
  payload: Record<string, unknown>;
  awards: { competency_id: string; xp: number; source: string }[];
}

export const ingestorsApi = {
  listTypes: () => api<{ types: IngestorType[] }>('/api/admin/ingestor-types'),

  listInstances: () => api<{ instances: IngestorInstance[] }>('/api/admin/ingestor-instances'),
  getInstance: (id: string) => api<{ instance: IngestorInstance }>(`/api/admin/ingestor-instances/${id}`),
  createInstance: (body: CreateInstanceBody) =>
    api<{ instance: IngestorInstance }>('/api/admin/ingestor-instances', { method: 'POST', body }),
  updateInstance: (id: string, body: UpdateInstanceBody) =>
    api<{ instance: IngestorInstance }>(`/api/admin/ingestor-instances/${id}`, { method: 'PATCH', body }),
  deleteInstance: (id: string) =>
    api(`/api/admin/ingestor-instances/${id}`, { method: 'DELETE' }),
  testConnection: (id: string) =>
    api<TestConnectionResult>(`/api/admin/ingestor-instances/${id}/test`, { method: 'POST' }),
  recentEvents: (id: string, limit = 50) =>
    api<{ events: IngestorEventRow[] }>(`/api/admin/ingestor-instances/${id}/events?limit=${limit}`),

  // M5.7+ raw-payload freeze toggle. Pins an event's `raw`
  // column so the TTL job (jobs/raw-ttl.ts) won't drop it.
  // Use case: "I'm actively debugging this event and need
  // the unredacted vendor payload." Audit-logged on the api
  // side. The raw itself is not restored by un-freezing —
  // if the TTL already dropped it, it's gone for good.
  setRawFrozen: (eventId: string, rawFrozen: boolean) =>
    api<{ id: string; raw_frozen: boolean }>(`/api/admin/ingestor-events/${eventId}/freeze`, {
      method: 'PATCH',
      body: { raw_frozen: rawFrozen },
    }),

  listActors: (id: string) =>
    api<{ actors: IngestorActor[] }>(`/api/admin/ingestor-instances/${id}/actors`),
  listUnmappedActors: (id: string) =>
    api<{ unmapped: UnmappedActor[] }>(`/api/admin/ingestor-instances/${id}/unmapped-actors`),
  createActor: (id: string, body: { external_id: string; user_id: string }) =>
    api<{ actor: IngestorActor }>(`/api/admin/ingestor-instances/${id}/actors`, { method: 'POST', body }),
  deleteActor: (id: string, externalId: string) =>
    api(`/api/admin/ingestor-instances/${id}/actors/${encodeURIComponent(externalId)}`, { method: 'DELETE' }),

  listRules: (teamId?: string) =>
    api<{ rules: ScoringRule[] }>(
      `/api/admin/scoring-rules${teamId ? `?team_id=${encodeURIComponent(teamId)}` : ''}`,
    ),
  createRule: (body: {
    team_id: string;
    priority: number;
    event_type: string;
    conditions: ConditionTree | null;
    splits: ScoringRuleSplit[];
    weight: number;
    enabled: boolean;
    description?: string | null;
    // M5.13: cooldown config. Both fields are optional in
    // the body so legacy callers keep working; missing
    // values default server-side to 0 / true.
    cooldown_seconds?: number;
    cooldown_use_target?: boolean;
  }) => api<{ rule: ScoringRule }>('/api/admin/scoring-rules', { method: 'POST', body }),
  updateRule: (id: string, body: Partial<ScoringRule>) =>
    api<{ rule: ScoringRule }>(`/api/admin/scoring-rules/${id}`, { method: 'PATCH', body }),
  deleteRule: (id: string) =>
    api(`/api/admin/scoring-rules/${id}`, { method: 'DELETE' }),

  reseedTeamRules: (teamId: string) =>
    api<{ ok: true; team_id: string }>(`/api/admin/teams/${teamId}/seed-scoring-rules`, { method: 'POST' }),

  listEventTypes: () => api<{ event_types: EventType[] }>('/api/admin/event-types'),
  createEventType: (body: { id: string; description?: string; source_ingestor_type?: string | null }) => api<{ event_type: EventType }>('/api/admin/event-types', { method: 'POST', body }),
  updateEventType: (id: string, body: Partial<Pick<EventType, 'description' | 'source_ingestor_type'>>) =>
    api<{ event_type: EventType }>(`/api/admin/event-types/${id}`, { method: 'PATCH', body }),
  deleteEventType: (id: string) => api(`/api/admin/event-types/${id}`, { method: 'DELETE' }),

  listBadges: () => api<{ badges: Badge[] }>('/api/admin/badges'),
  createBadge: (body: {
    id: string;
    name: string;
    description: string;
    rarity: BadgeRarity;
    icon: string;
    criteria: ConditionTree | null;
    enabled: boolean;
    is_loot: boolean;
    drop_weight: number;
    // M5.x Surface-2 (ADR-0023). NULL = Surface-1
    // quest-completion pool; non-null = Surface-2
    // event-driven pool for that ingestor_event.type.
    // Empty-as-null on the form side; the api enforces
    // mutual exclusion with is_loot.
    loot_event_type:
      | 'pr.closed'
      | 'issue.transition'
      | 'pr.review.submitted'
      | 'kudos.given'
      | 'issue.created'
      | 'pr.opened'
      | 'issue.commented'
      | null;
  }) =>
    api<{ badge: Badge }>('/api/admin/badges', { method: 'POST', body }),
  updateBadge: (id: string, body: Partial<Badge>) =>
    api<{ badge: Badge }>(`/api/admin/badges/${id}`, { method: 'PATCH', body }),
  deleteBadge: (id: string) => api(`/api/admin/badges/${id}`, { method: 'DELETE' }),

  // M5.10: condition tester. Eval a (payload, conditions)
  // pair the same way the scoring engine does. The shape
  // is permissive because the condition tree is recursive
  // (ConditionLeaf | ConditionAll | ConditionAny |
  // ConditionNot) and the UI builder is the source of truth
  // for shape.
  evalCondition: (body: {
    payload: Record<string, unknown>;
    conditions: ConditionTree | null;
  }) =>
    api<{
      result: boolean;
      trace: ConditionTraceNode | null;
    }>('/api/admin/condition-tester/eval', { method: 'POST', body }),
};

// The trace returned by the condition tester endpoint.
// Mirrors apps/api/src/scoring/conditions.ts TraceNode.
// `children` and `child` are intentionally marked optional
// here so a malformed trace from the server (e.g. mid-
// rollout mismatch) renders as a "malformed trace" hint
// instead of black-screening the page.
export type ConditionTraceNode =
  | { kind: 'leaf'; condition: ConditionTree; actual: unknown; result: boolean }
  | { kind: 'all'; condition: ConditionTree; children?: ConditionTraceNode[]; result: boolean }
  | { kind: 'any'; condition: ConditionTree; children?: ConditionTraceNode[]; result: boolean }
  | { kind: 'not'; condition: ConditionTree; child?: ConditionTraceNode; result: boolean };
